import { createStore, type StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";
import { api as realApi } from "./http";
import { realtime as realRt, type Realtime } from "./realtime";
import type { Frame, NotificationItem, NotificationPage } from "./types";

export interface NotifState {
  items: NotificationItem[];
  unread: number;
  loaded: boolean;
  hasMore: boolean;
  next: string;
  error: string;
}

export interface NotifDeps {
  api: typeof realApi;
  rt: Pick<Realtime, "onFrame" | "onState">;
}

export type NewNotificationHandler = (item: NotificationItem, silent: boolean) => void;

const initial = (): NotifState => ({ items: [], unread: 0, loaded: false, hasMore: false, next: "", error: "" });

const byNewest = (a: NotificationItem, b: NotificationItem) => b.createdMs - a.createdMs;

export const mergeItems = (list: NotificationItem[], add: NotificationItem[]): NotificationItem[] => {
  const map = new Map(list.map((i) => [i.id, i]));
  add.forEach((i) => map.set(i.id, { ...map.get(i.id), ...i }));
  return [...map.values()].sort(byNewest);
};

export function createNotificationStore(deps: NotifDeps) {
  const store: StoreApi<NotifState> = createStore<NotifState>(() => initial());
  const handlers = new Set<NewNotificationHandler>();
  const set = store.setState;
  const get = store.getState;

  const actions = {
    /** Start listening. Returns a stop function. */
    start(): () => void {
      const offFrame = deps.rt.onFrame((f: Frame) => actions.handleFrame(f));
      // after a reconnect, anything missed is fetched again
      const offState = deps.rt.onState((st, reopened) => {
        if (st === "open" && reopened) void actions.load();
      });
      void actions.load();
      return () => {
        offFrame();
        offState();
      };
    },

    onNew(h: NewNotificationHandler): () => void {
      handlers.add(h);
      return () => handlers.delete(h);
    },

    async load(): Promise<void> {
      try {
        const p = await deps.api<NotificationPage>("/api/v1/notifications", { query: { limit: 50, includeQuiet: undefined } });
        set({ items: p.items ?? [], unread: p.unread ?? 0, hasMore: !!p.hasMore, next: p.next ?? "", loaded: true, error: "" });
      } catch (e) {
        set({ loaded: true, error: e instanceof Error ? e.message : "Could not load notifications" });
      }
    },

    async loadMore(): Promise<void> {
      const { hasMore, next } = get();
      if (!hasMore || !next) return;
      const p = await deps.api<NotificationPage>("/api/v1/notifications", { query: { limit: 50, after: next } });
      set((s) => ({ items: mergeItems(s.items, p.items ?? []), hasMore: !!p.hasMore, next: p.next ?? "", unread: p.unread ?? s.unread }));
    },

    async markRead(id: string, read = true): Promise<void> {
      const it = get().items.find((i) => i.id === id);
      if (!it || (read && it.readMs > 0) || (!read && it.readMs === 0)) return;
      set((s) => ({
        items: s.items.map((i) => (i.id === id ? { ...i, readMs: read ? Date.now() : 0 } : i)),
        unread: Math.max(0, s.unread + (read ? -1 : 1)),
      }));
      try {
        await deps.api(`/api/v1/notifications/${encodeURIComponent(id)}/${read ? "read" : "unread"}`, { method: "POST" });
      } catch {
        void actions.load(); // put the list back to what the server says
      }
    },

    async markAllRead(convId?: string): Promise<void> {
      const t = Date.now();
      set((s) => ({
        items: s.items.map((i) => (i.readMs === 0 && (!convId || i.convId === convId) ? { ...i, readMs: t } : i)),
        unread: convId ? s.unread : 0,
      }));
      try {
        await deps.api("/api/v1/notifications/read-all", { method: "POST", body: convId ? { convId } : {} });
      } catch {
        void actions.load();
      }
    },

    async remove(id: string): Promise<void> {
      set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
      try {
        await deps.api(`/api/v1/notifications/${encodeURIComponent(id)}`, { method: "DELETE" });
      } catch {
        void actions.load();
      }
    },

    async sendTest(): Promise<void> {
      await deps.api("/api/v1/notifications/test", { method: "POST", body: {} });
    },

    handleFrame(f: Frame): void {
      const d = (f.d ?? {}) as Record<string, unknown>;
      switch (f.t) {
        case "notification.new": {
          const item = d.item as NotificationItem | undefined;
          if (!item) return;
          const silent = d.silent === true;
          set((s) => ({ items: mergeItems(s.items, [item]), unread: typeof d.unread === "number" ? d.unread : s.unread + 1 }));
          handlers.forEach((h) => {
            try {
              h(item, silent);
            } catch (e) {
              console.error("notification handler failed", e);
            }
          });
          return;
        }
        case "notification.read": {
          const ids = Array.isArray(d.ids) ? (d.ids as string[]) : [];
          const read = d.read !== false;
          const t = Date.now();
          set((s) => ({
            items: s.items.map((i) => (ids.includes(i.id) ? { ...i, readMs: read ? i.readMs || t : 0 } : i)),
            unread: typeof d.unread === "number" ? d.unread : s.unread,
          }));
          return;
        }
        case "notification.deleted": {
          const ids = Array.isArray(d.ids) ? (d.ids as string[]) : [];
          set((s) => ({ items: s.items.filter((i) => !ids.includes(i.id)), unread: typeof d.unread === "number" ? d.unread : s.unread }));
          return;
        }
        default:
      }
    },

    reset(): void {
      set(initial());
    },
  };
  return { store, actions };
}

const inst = createNotificationStore({ api: realApi, rt: realRt });
export const notifStore = inst.store;
export const notifications = inst.actions;
export const useNotifications = <T,>(selector: (s: NotifState) => T): T => useStore(notifStore, selector);
